import React, { useEffect, useRef } from 'react';
import Head from 'next/head';
import tailwindConfig from '@/tailwind.screens';
import { isValidHttpUrl, loadLazyImage, getUrlExtension } from '@/utils';
import { ImageModel } from '@/types';
import classNames from 'classnames';
import { Box } from '@/components';
import { AnimationProps } from '../animation/Box';

function screenArray() {
  const screenArray = [{ key: 'xs', value: tailwindConfig.screens.sm }];

  Object.entries(tailwindConfig.screens).forEach(([key, value]) => {
    if (typeof value !== 'object') {
      screenArray.push({
        key,
        value,
      });
    }
  });
  return screenArray;
}

function imageSize(sizeObject: { [x: string]: number | null }, breakpoint: string) {
  if (sizeObject) {
    if (breakpoint in sizeObject) {
      return sizeObject[breakpoint] && sizeObject[breakpoint] > 0 ? sizeObject[breakpoint] : null;
    }
    const sList = screenArray().map((item) => item.key);

    let startIndex = sList.indexOf(breakpoint);
    while (startIndex > -1) {
      startIndex--;
      if (sList[startIndex] in sizeObject) {
        return sizeObject[sList[startIndex]];
      }
    }
  }
  return null;
}

const pixelDensities = [1, 2, 3];

export type ResponsiveImageSizesType = {
  xs?: number | null;
  sm?: number | null;
  md?: number | null;
  lg?: number | null;
  xl?: number | null;
  xxl?: number | null;
};
export interface IResponsiveImageProps
  extends AnimationProps,
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> {
  image: ImageModel;
  widths?: ResponsiveImageSizesType;
  heights?: ResponsiveImageSizesType;
  preload?: boolean;
  cdnProps?: object;
  dataNotLazy?: boolean;
  imgProps?: React.HTMLAttributes<HTMLImageElement>;
  className?: string;
}

const ResponsiveImage = ({
  animate = false,
  animation,
  image: _image,
  widths,
  heights,
  preload,
  cdnProps = {},
  dataNotLazy,
  imgProps,
  className = '',
  ...props
}: IResponsiveImageProps) => {
  const { className: imageClassName, ..._imgProps } = imgProps || {};

  const image: IResponsiveImageProps['image'] | null = _image;

  const pictureRef = useRef<HTMLPictureElement>(null);

  useEffect(() => {
    const observerRefValue = pictureRef.current;

    if (!observerRefValue) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadLazyImage(entry.target as HTMLElement);
        observer.unobserve(observerRefValue);
      }
    });

    if (observerRefValue?.getBoundingClientRect().top < window.screen.height) {
      loadLazyImage(pictureRef.current);
    } else if (observerRefValue) {
      observer.observe(observerRefValue);
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
    };
  }, []);

  if (!image) return;

  const isSvg = getUrlExtension(image.imageUrl) === 'svg';

  const screens = screenArray();

  const buildSrcSet = (breakpoint: string, format: string | undefined = undefined) => {
    const srcSet = pixelDensities.map((pd) => buildImageUrl(breakpoint, format, pd));
    return srcSet;
  };

  const buildImageUrl = (
    breakpoint: string,
    format: string | undefined = undefined,
    pixelDensity: number | undefined = 1,
  ) => {
    if (image.imageUrl) {
      const validHttpUrl = isValidHttpUrl(image.imageUrl);

      const imageUrl = new URL(image.imageUrl, !validHttpUrl ? 'http://fake-base.com' : undefined);

      imageUrl.protocol = 'https:';

      if (widths) {
        const width = imageSize(widths, breakpoint);
        if (width) {
          imageUrl.searchParams.set('width', width * (pixelDensity - 1 || 1) + '');
        }
      }
      if (heights) {
        const height = imageSize(heights, breakpoint);
        if (height) {
          imageUrl.searchParams.set('height', height * (pixelDensity - 1 || 1) + '');
        }
      }

      if (format) {
        imageUrl.searchParams.set('format', format);
      }

      if (cdnProps) {
        Object.entries(cdnProps).forEach(([key, value]) => {
          imageUrl.searchParams.set(key, value);
        });
      }
      if (validHttpUrl) {
        return imageUrl.href;
      }
      return imageUrl.pathname + imageUrl.search + (pixelDensity ? ` ${pixelDensity}x` : '');
    }
    return '';
  };

  const sourceArray: Array<React.JSX.Element> = [];
  const preloadLinks: Array<React.JSX.Element> = [];

  screens.forEach((screen, i) => {
    let media;
    if (i === 0) {
      media = `(max-width: ${parseInt(screen.value, 10) - 1}px)`;
    } else if (i === screens.length - 1) {
      media = `(min-width: ${screen.value})`;
    } else {
      media = `(min-width: ${screen.value}) and (max-width: ${parseInt(screens[i + 1].value, 10) - 1}px)`;
    }

    const sizes = pixelDensities.map((pd) => `${pd}x`).join(', ');

    const srcSetWebp = buildSrcSet(screen.key, 'webp');
    const srcSet = buildSrcSet(screen.key);

    if (!isSvg) {
      sourceArray.push(
        <source
          key={i}
          sizes={sizes}
          {...(preload || dataNotLazy ? { srcSet: srcSetWebp.join(', ') } : { 'data-srcset': srcSetWebp.join(', ') })}
          media={media}
          type="image/webp"
        />,
      );
    }
    sourceArray.push(
      <source
        key={i + 10}
        sizes={sizes}
        {...(preload || dataNotLazy ? { srcSet: srcSet.join(', ') } : { 'data-srcset': srcSet.join(', ') })}
        media={media}
      />,
    );

    if (preload) {
      preloadLinks.push(
        <link
          key={i}
          rel="preload"
          as="image"
          fetchPriority="high"
          href={buildImageUrl('xs', 'webp')}
          imageSrcSet={isSvg ? srcSet.join(', ') : srcSetWebp.join(', ')}
          media={media}
        />,
      );
    }
  });

  const pictureElement = (
    <picture
      key={image.mediaId}
      className={classNames('responsive-image h-full w-full', className, !preload && !dataNotLazy && 'lazy-image')}
      data-not-lazy={dataNotLazy}
      ref={pictureRef}
      {...props}
    >
      {sourceArray}
      <img
        {...(preload || dataNotLazy
          ? { src: buildImageUrl('lg') }
          : {
            'data-src': buildImageUrl('lg'),
            src: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
          })}
        alt={image.imageAlt || image.caption || ''}
        className={classNames('block h-full w-full object-cover', imageClassName)}
        {..._imgProps}
      />
    </picture>
  );

  return (
    <>
      <Head>{preloadLinks}</Head>
      {!animate && !animation ? (
        pictureElement
      ) : (
        <Box className="h-full w-full" animation={animation}>
          {pictureElement}
        </Box>
      )}
    </>
  );
};

export default ResponsiveImage;
